import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import api from "@/lib/api";
import { toast } from "sonner";

const SECTIONS = [
  {
    title: "Bureau & Income Rules",
    fields: [
      { key: "min_cibil", label: "Minimum CIBIL score" },
      { key: "min_monthly_income", label: "Minimum monthly income (₹)" },
      { key: "max_foir", label: "Maximum FOIR (0–1)", step: 0.01 },
      { key: "max_pti", label: "Maximum PTI (0–1)", step: 0.01 },
      { key: "min_avg_bank_balance", label: "Min avg bank balance (₹)" },
      { key: "max_emi_bounces_6m", label: "Max EMI bounces (6m)" },
      { key: "coborrower_trigger_foir", label: "Co-borrower trigger FOIR", step: 0.01 },
      { key: "thin_file_max_ltv", label: "Thin-file max LTV (0–1)", step: 0.01 },
    ],
    switches: [{ key: "allow_thin_file", label: "Allow thin-file / no-hit borrowers" }],
  },
  {
    title: "Vehicle Rules",
    fields: [
      { key: "max_vehicle_age", label: "Max vehicle age (years)" },
      { key: "max_vehicle_age_at_maturity", label: "Max age at loan maturity (years)" },
      { key: "max_km", label: "Max odometer (km)" },
      { key: "max_ownership_count", label: "Max ownership count" },
      { key: "repair_cost_threshold_pct", label: "Repair cost threshold (% of value, 0–1)", step: 0.01 },
    ],
  },
  {
    title: "Pricing & Terms",
    fields: [
      { key: "base_roi", label: "Base ROI (% p.a.)", step: 0.1 },
      { key: "min_tenure_months", label: "Min tenure (months)" },
      { key: "max_tenure_months", label: "Max tenure (months)" },
      { key: "min_down_payment_pct", label: "Min down payment (0–1)", step: 0.01 },
      { key: "processing_fee_pct", label: "Processing fee (0–1)", step: 0.001 },
      { key: "min_ticket_size", label: "Min ticket size (₹)" },
      { key: "max_ticket_size", label: "Max ticket size (₹)" },
    ],
  },
  {
    title: "Stipulations & Governance",
    fields: [
      { key: "manual_approval_above_amount", label: "Manual approval above (₹)" },
    ],
    switches: [
      { key: "insurance_required", label: "Comprehensive insurance required" },
      { key: "hypothecation_required", label: "RTO hypothecation required" },
    ],
  },
];

const GRADES = ["A", "B", "C", "D", "Reject"];

export default function PolicyConfigTab() {
  const [policy, setPolicy] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/policy").then(r => setPolicy(r.data)).catch(() => toast.error("Could not load policy")); }, []);

  if (!policy) return <div className="text-sm text-slate-500">Loading policy…</div>;

  const setField = (key, value) => setPolicy(p => ({ ...p, [key]: value }));
  const setGradeLtv = (g, value) => setPolicy(p => ({ ...p, ltv_by_grade: { ...p.ltv_by_grade, [g]: value } }));

  const save = async () => {
    setBusy(true);
    try {
      const { key, version, created_at, updated_at, updated_by, ...updates } = policy;
      // coerce numerics
      const numeric = {};
      Object.entries(updates).forEach(([k, v]) => {
        numeric[k] = typeof v === "string" && v !== "" && !isNaN(Number(v)) ? Number(v) : v;
      });
      if (numeric.ltv_by_grade) {
        Object.keys(numeric.ltv_by_grade).forEach(g => { numeric.ltv_by_grade[g] = Number(numeric.ltv_by_grade[g]); });
      }
      const { data } = await api.put("/policy", { updates: numeric, reason });
      setPolicy(data);
      setReason("");
      toast.success(`Policy saved — now v${data.version}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="policy-config-tab">
      <div className="text-xs text-slate-500">Version {policy.version} {policy.updated_by ? `· last updated by ${policy.updated_by}` : ""}</div>

      {SECTIONS.map(section => (
        <Card key={section.title} className="p-5 border-slate-200">
          <div className="text-sm font-semibold text-slate-900 mb-4">{section.title}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {section.fields.map(f => (
              <div key={f.key}>
                <Label className="text-xs">{f.label}</Label>
                <Input type="number" step={f.step || 1} value={policy[f.key] ?? ""} onChange={e => setField(f.key, e.target.value)} data-testid={`policy-field-${f.key}`} className="mt-1" />
              </div>
            ))}
            {(section.switches || []).map(s => (
              <div key={s.key} className="flex items-center gap-2 pt-5">
                <Switch checked={!!policy[s.key]} onCheckedChange={v => setField(s.key, v)} data-testid={`policy-switch-${s.key}`} />
                <span className="text-xs text-slate-700">{s.label}</span>
              </div>
            ))}
          </div>
        </Card>
      ))}

      <Card className="p-5 border-slate-200">
        <div className="text-sm font-semibold text-slate-900 mb-4">Max LTV by CarDr Grade</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {GRADES.map(g => (
            <div key={g}>
              <Label className="text-xs">Grade {g}</Label>
              <Input type="number" step={0.01} value={policy.ltv_by_grade?.[g] ?? ""} onChange={e => setGradeLtv(g, e.target.value)} data-testid={`policy-ltv-grade-${g}`} className="mt-1" />
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5 border-teal-200 bg-teal-50/50">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-64">
            <Label className="text-xs">Reason for change (recorded in policy audit log)</Label>
            <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Board approval dated …" data-testid="policy-reason-input" className="mt-1 bg-white" />
          </div>
          <Button onClick={save} disabled={busy} className="bg-teal-600 hover:bg-teal-700" data-testid="policy-save-btn">Save policy</Button>
        </div>
      </Card>
    </div>
  );
}
